import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import Banner1 from "../../assets/img/website-images/Blogdetailsbanner1.png";

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
  hidden: { opacity: 0, scale: 1.04 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

const paraStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};

const paraVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: "easeOut" },
  },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  return (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: "easeOut", delay }}
    >
      {children}
    </motion.p>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogOne() {
  /* Hero image */
  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* Title */
  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* GSAP: hero image reveal with clip-path wipe */
  const imgWrapRef = useRef(null);
  useEffect(() => {
    if (heroInView && imgWrapRef.current) {
      gsap.fromTo(
        imgWrapRef.current,
        { clipPath: "inset(0 100% 0 0)", opacity: 1 },
        {
          clipPath: "inset(0 0% 0 0)",
          duration: 1.1,
          ease: "power3.inOut",
          delay: 0.1,
        },
      );
    }
  }, [heroInView]);

  /* GSAP: subtle parallax on hero image while scrolling */
  const heroImgRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!heroImgRef.current) return;
      const rect = heroImgRef.current.getBoundingClientRect();
      const progress = 1 - rect.bottom / (window.innerHeight + rect.height);
      gsap.set(heroImgRef.current, { y: progress * 30 });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* GSAP: decorative reading-progress bar at top */
  const progressRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!progressRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      gsap.set(progressRef.current, { width: `${progress}%` });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Reading progress bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          background: "transparent",
          zIndex: 9999,
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <div
          ref={progressRef}
          style={{
            height: "100%",
            width: "0%",
            background: "linear-gradient(90deg, #1E5AA8, #F27024)",
            borderRadius: "0 2px 2px 0",
            transition: "width 0.1s linear",
          }}
        />
      </div>

      <section className="project-detail">
        <div className="container">
          {/* ── Hero Image ── */}
          <div className="upper-box" ref={heroRef}>
            <motion.div
              variants={heroVariants}
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              style={{ borderRadius: "12px", overflow: "hidden" }}
            >
              <div
                ref={imgWrapRef}
                style={{ overflow: "hidden", borderRadius: "12px" }}
              >
                <figure
                  className="image"
                  style={{ margin: 0, overflow: "hidden" }}
                >
                  <img
                    ref={heroImgRef}
                    src={Banner1}
                    alt="Blog Banner"
                    style={{
                      display: "block",
                      width: "100%",
                      transformOrigin: "center",
                    }}
                  />
                </figure>
              </div>
            </motion.div>
          </div>

          {/* ── Article Content ── */}
          <div className="lower-content2">
            <div className="row">
              <div className="text-column col-lg-12 col-md-12 col-sm-12">
                <div className="s-about-content">
                  {/* Title */}
                  <motion.h2
                    ref={titleRef}
                    variants={titleVariants}
                    initial="hidden"
                    animate={titleInView ? "visible" : "hidden"}
                  >
                    How to Choose the Right Pharmacy College After 12th
                  </motion.h2>

                  <AnimPara delay={0}>
                    Choosing the right pharmacy college after completing 12th
                    grade is one of the most important decisions for students
                    who want to build a successful career in the healthcare and
                    pharmaceutical industry. With many institutions offering
                    pharmacy programs, it is essential to carefully evaluate
                    your options and select a college that provides quality
                    education, practical training, and strong career support.
                  </AnimPara>

                  <AnimPara delay={0}>
                    The first factor to consider is the college’s affiliation
                    and approval. A good pharmacy college should be affiliated
                    with a recognized university and approved by relevant
                    regulatory bodies. These approvals ensure that the
                    curriculum meets national education standards and that the
                    degree you earn will be recognized by employers and higher
                    education institutions. Accreditation also reflects the
                    institution’s commitment to maintaining academic quality and
                    professional standards.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Another important aspect is the availability of modern
                    laboratories and practical learning facilities. Pharmacy
                    education is not limited to classroom lectures — it requires
                    hands-on training in subjects such as pharmaceutics,
                    pharmacology, and pharmaceutical chemistry. Well-equipped
                    laboratories help students understand real-world
                    pharmaceutical processes and develop the technical skills
                    required in the industry.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Experienced and supportive faculty members also play a
                    crucial role in shaping a student’s academic journey.
                    Qualified teachers guide students through complex scientific
                    concepts, mentor them during practical sessions, and help
                    them prepare for future careers. Institutions that encourage
                    interactive learning, research activities, and professional
                    development opportunities create a stronger foundation for
                    long-term success.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Students should also consider the college’s placement
                    support, internship opportunities, and industry exposure.
                    Internships provide valuable real-world experience and help
                    students understand workplace expectations. Colleges that
                    maintain connections with pharmaceutical companies,
                    hospitals, and healthcare organizations can offer better
                    career opportunities and professional growth.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Finally, the overall campus environment, safety, and student
                    support services are essential factors when selecting a
                    pharmacy college. A positive learning environment encourages
                    students to focus on their studies, participate in
                    extracurricular activities, and develop confidence in their
                    abilities. By carefully evaluating these factors, students
                    can make informed decisions and choose a pharmacy college
                    that supports their academic goals and future career
                    aspirations.
                  </AnimPara>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogOne;
